import { copyToClipboard } from '@mi-front/core';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button, ButtonProps, IconButton, IconButtonProps } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

export interface CopyBtnProps {
  copyValue: string;
  children?: ReactNode;
  /** Optional start icon; defaults to ContentCopy. Used as the mobile IconButton content too. */
  icon?: ReactNode;
  /** When true, renders an IconButton instead of a labeled Button. */
  mobile?: boolean;
  onCopied?: () => void;
  size?: ButtonProps['size'] | IconButtonProps['size'];
  variant?: ButtonProps['variant'];
  color?: ButtonProps['color'];
  sx?: ButtonProps['sx'];
  disabled?: boolean;
}

/**
 * Copies `copyValue` to the clipboard and briefly shows a "Copied" state.
 */
export function CopyBtn(p: CopyBtnProps) {
  const {
    copyValue,
    children,
    icon,
    mobile,
    onCopied,
    size = 'small',
    variant = 'outlined',
    color = 'secondary',
    sx,
    disabled = false,
  } = p;
  const { $t } = useIntl();
  const idleIcon = icon ?? <ContentCopyIcon />;

  let textChangeTimeout: number = null!;
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    return () => {
      if (textChangeTimeout) {
        clearTimeout(textChangeTimeout);
      }
    };
  }, [textChangeTimeout]);

  const handleClick = () => {
    setCopied(true);
    copyToClipboard(copyValue);
    onCopied?.();

    textChangeTimeout = window.setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  if (mobile) {
    return (
      <IconButton
        size={size as IconButtonProps['size']}
        onClick={handleClick}
        disabled={copied || disabled}
        color={color as IconButtonProps['color']}
        sx={sx}
      >
        {copied ? <CheckIcon /> : idleIcon}
      </IconButton>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      color={color}
      onClick={handleClick}
      disabled={copied || disabled}
      startIcon={copied ? <CheckIcon /> : idleIcon}
      sx={{ minWidth: '8rem', ...sx }}
    >
      {copied ? $t({ id: 'common.copyBtn.copied', defaultMessage: 'Copied' }) : children}
    </Button>
  );
}
